import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { updateUserProfile } from "../../api/controller/userProfile";

const toRegionIdList = (regions) => {
  if (!regions) return [];
  if (Array.isArray(regions)) {
    return regions
      .map((item) => {
        if (!item) return null;
        if (typeof item === "string") return item;
        const id = item._id || item.id || item.cityId || item.city;
        if (id) return id.toString();
        if (typeof item === "number") return item.toString();
        return null;
      })
      .filter(Boolean);
  }
  if (typeof regions === "string") {
    return regions
      .split(",")
      .map((entry) => entry.trim())
      .filter(Boolean);
  }
  return [];
};

const ProfileDialog = ({ user, onSave, showModal, setShowModal, regionOptions }) => {
  const [formData, setFormData] = useState({
    fullName: user.fullName,
    organization: user.organization || "",
    jobTitle: user.jobTitle || "",
    phoneNumber: user.phoneNumber || "",
    regions: toRegionIdList(user.regions),
    profilePhoto: user.profilePhoto,
    password: "",
  });

  useEffect(() => {
    if (!showModal) return;
    setFormData({
      fullName: user.fullName,
      organization: user.organization || "",
      jobTitle: user.jobTitle || "",
      phoneNumber: user.phoneNumber || "",
      regions: toRegionIdList(user.regions),
      profilePhoto: user.profilePhoto,
      password: "",
    });
  }, [user, showModal]);

  const normalizedRegionOptions = useMemo(
    () => (Array.isArray(regionOptions) ? regionOptions : []).map((option) => ({
      id: option.id?.toString() || "",
      label: option.label || option.id?.toString() || "",
    })).filter((option) => option.id && option.label),
    [regionOptions]
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({ ...prev, profilePhoto: e.target.files[0] }));
  };

  const toggleRegion = (regionId) => {
    if (!regionId) return;
    setFormData((prev) => {
      const current = Array.isArray(prev.regions) ? prev.regions : [];
      const id = regionId.toString();
      const exists = current.includes(id);
      const next = exists ? current.filter((value) => value !== id) : [...current, id];
      return { ...prev, regions: next };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const updatedProfile = await updateUserProfile({
        fullName: formData.fullName,
        organization: formData.organization,
        jobTitle: formData.jobTitle,
        phoneNumber: formData.phoneNumber,
        regions: Array.isArray(formData.regions) ? formData.regions : [],
        profilePhoto: formData.profilePhoto,
        password: formData.password,
      });
      onSave(updatedProfile);
      setShowModal(false);
    } catch (error) {
      console.error("Failed to update profile:", error);
    }
  };

  if (!showModal) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div
        className="absolute inset-0 bg-black opacity-50"
        onClick={() => setShowModal(false)}
      ></div>
      <div className="relative bg-white rounded-lg p-6 w-full max-w-md shadow-lg z-10">
          <button
            type="button"
            onClick={() => setShowModal(false)}
            className="absolute top-3 right-3 text-gray-500 hover:text-gray-800 text-3xl font-bold leading-none"
            aria-label="Close dialog"
          >
            &times;
          </button>
        <h2 className="text-xl font-bold mb-4">Edit Profile</h2>
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Full Name:</label>
            <input
              type="text"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg p-2"
              required
            />
          </div>
          {/* <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Date of Birth:</label>
            <input
              type="date"
              name="dob"
              value={formData.dob}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg p-2"
              required
            />
          </div> */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Organization:</label>
            <input
              type="text"
              name="organization"
              value={formData.organization}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg p-2"
              placeholder="Enter your organization"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Job Title:</label>
            <input
              type="text"
              name="jobTitle"
              value={formData.jobTitle}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg p-2"
              placeholder="Enter your job title"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Phone Number:</label>
            <input
              type="tel"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg p-2"
              placeholder="Enter your phone number"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Regions:</label>
            <div className="border border-gray-300 rounded-lg p-3 max-h-40 overflow-y-auto">
              {normalizedRegionOptions.length ? (
                normalizedRegionOptions.map((option) => (
                  <label key={option.id} className="flex items-center gap-2 text-sm text-gray-700 mb-2 last:mb-0">
                    <input
                      type="checkbox"
                      value={option.id}
                      checked={Array.isArray(formData.regions) && formData.regions.includes(option.id)}
                      onChange={() => toggleRegion(option.id)}
                      className="h-4 w-4 text-blue-600"
                    />
                    <span>{option.label}</span>
                  </label>
                ))
              ) : (
                <p className="text-sm text-gray-500">No regions available. Please try again later.</p>
              )}
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Profile Photo:</label>
            <input
              type="file"
              name="profilePhoto"
              onChange={handleFileChange}
              className="w-full border border-gray-300 rounded-lg p-2"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Password:</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg p-2"
            />
          </div>
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => setShowModal(false)}
              className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-blue-600 text-white px-4 py-2 rounded-lg"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

ProfileDialog.propTypes = {
  user: PropTypes.shape({
    fullName: PropTypes.string.isRequired,
    organization: PropTypes.string,
    profilePhoto: PropTypes.string.isRequired,
    userName: PropTypes.string.isRequired,
    userType: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    userId: PropTypes.string.isRequired,
    jobTitle: PropTypes.string,
    phoneNumber: PropTypes.string,
    regions: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.any),
      PropTypes.string,
    ]),
  }).isRequired,
  onSave: PropTypes.func.isRequired,
  showModal: PropTypes.bool.isRequired,
  setShowModal: PropTypes.func.isRequired,
  regionOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
};

ProfileDialog.defaultProps = {
  regionOptions: [],
};

export default ProfileDialog;